import { useQuery } from "@apollo/client";
import { Filter, Loader2, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import type { LoyaltyTier } from "@/gql/graphql";
import { LOYALTY_TIERS_QUERY } from "@/graphql/loyalty";
import { useConfig } from "@/hooks/configuration";
import { useIsMobile } from "@/lib/hooks/use-mobile";
import { useSalonStore } from "@/lib/store/salon";
import { useCustomersUIStore } from "../../store/use-customers-ui-store";

interface CustomerFiltersBarProps {
  loading: boolean;
  hasFilters: boolean;
  isCustomerAccessLimited?: boolean;
}

// Export the mobile search field as a separate component
export function MobileSearchField({ loading }: { loading: boolean }) {
  const { t } = useTranslation("customers");
  const { searchTerm, setSearchTerm } = useCustomersUIStore();

  return (
    <div className="p-0">
      <div className="relative">
        {loading ? (
          <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        ) : (
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        )}
        <Input
          placeholder={t("filters.searchPlaceholderDetailed")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-background"
          autoFocus
        />
      </div>
    </div>
  );
}

export function CustomerFiltersBar({
  loading,
  hasFilters,
  isCustomerAccessLimited = false,
}: CustomerFiltersBarProps) {
  const { t } = useTranslation("customers");
  const isMobile = useIsMobile();
  const { value: loyaltyEnabled = false } = useConfig("loyalty.enabled");
  const {
    searchTerm,
    activeTierFilters,
    setSearchTerm,
    toggleTierFilter,
    toggleMobileSearch,
    clearAllFilters,
  } = useCustomersUIStore();

  const { currentSalon } = useSalonStore();
  const { data: loyaltyTiersData } = useQuery(LOYALTY_TIERS_QUERY, {
    variables: { franchiseId: currentSalon?.franchiseId || "" },
    skip: !currentSalon?.franchiseId || !loyaltyEnabled,
  });

  const loyaltyTiers = loyaltyTiersData?.loyaltyTiers || [];
  const activeFilterCount = activeTierFilters.size;
  const hasActiveFilters = activeFilterCount > 0;

  // For mobile, return both the filter controls and search field when toggled
  if (isMobile) {
    return (
      <div className="flex items-center gap-2">
        {/* Mobile Search Toggle - Hidden when customer access is limited */}
        {!isCustomerAccessLimited && (
          <Button variant="ghost" size="sm" onClick={toggleMobileSearch} className="h-9 w-9 p-0">
            <Search className="w-4 h-4" />
          </Button>
        )}

        {/* Mobile Filter Menu - Only show if loyalty is enabled */}
        {loyaltyEnabled && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-9 w-9 p-0 relative">
                <Filter className="w-4 h-4" />
                {hasActiveFilters && (
                  <Badge
                    variant="secondary"
                    className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-xs border"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>{t("filters.filterByTier")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {loyaltyTiers.map((tier: LoyaltyTier) => (
                <DropdownMenuCheckboxItem
                  key={tier.id}
                  checked={activeTierFilters.has(tier.id)}
                  onCheckedChange={() => toggleTierFilter(tier.id)}
                >
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: tier.color }}
                  />
                  <span>{tier.name}</span>
                </DropdownMenuCheckboxItem>
              ))}

              {hasFilters && (
                <>
                  <DropdownMenuSeparator />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="w-full justify-start h-8 px-2 py-1.5"
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t("filters.clearAllFilters")}
                  </Button>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="flex items-center gap-3">
      {/* Search Bar - Desktop - Hidden when customer access is limited */}
      {!isCustomerAccessLimited && (
        <div className="relative flex-1 max-w-md">
          {loading ? (
            <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
          ) : (
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          )}
          <Input
            placeholder={t("filters.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-9 bg-background"
          />
        </div>
      )}

      {/* Desktop Filter Menu - Only show if loyalty is enabled */}
      {loyaltyEnabled && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 px-3">
              <Filter className="w-4 h-4 mr-2" />
              {t("filters.filter")}
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>{t("filters.filterByTier")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {loyaltyTiers.map((tier: LoyaltyTier) => (
              <DropdownMenuCheckboxItem
                key={tier.id}
                checked={activeTierFilters.has(tier.id)}
                onCheckedChange={() => toggleTierFilter(tier.id)}
              >
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: tier.color }}
                />
                <span>{tier.name}</span>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Desktop Clear Filters Button - Only show if loyalty is enabled and has filters */}
      {loyaltyEnabled && hasFilters && (
        <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-9 px-3">
          <X className="w-4 h-4 mr-1" />
          {t("filters.clear")}
        </Button>
      )}
    </div>
  );
}
